<template>
  <div class="threat-visualizer" :class="{ 'fullscreen-mode': isFullscreen, 'dark-theme': isDarkTheme }">
    <!-- Fullscreen Overlay -->
    <div v-if="isFullscreen" class="fullscreen-overlay" :class="{ 'dark-theme': isDarkTheme }" :style="{ left: sidebarWidth }">
      <div class="fullscreen-container">
        <div class="fullscreen-header" :class="{ 'dark-theme': isDarkTheme }">
          <div class="fullscreen-header__left">
            <div class="visualizer-title">Threat Landscape Visualization</div>
            <div class="visualizer-subtitle">
              {{ dynamicSubtitle }}
            </div>
          </div>
          <div class="fullscreen-header__right">
            <!-- View Tabs -->
            <div class="view-tabs">
              <button
                class="tab-btn"
                :class="{ active: activeView === 'v1' }"
                @click="activeView = 'v1'"
              >
                <v-icon small class="mr-1">mdi-chart-sankey-variant</v-icon>
                Flow View
              </button>
              <button
                class="tab-btn"
                :class="{ active: activeView === 'v2' }"
                @click="activeView = 'v2'"
              >
                <v-icon small class="mr-1">mdi-graph-outline</v-icon>
                Graph View
              </button>
            </div>

            <!-- CVE Selector -->
            <v-menu v-model="fullscreenCveMenuOpen" :close-on-content-click="false" offset-y max-width="320" min-width="280">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" class="cve-selector-wrapper">
                  <v-text-field :value="selectorDisplayText" readonly dense outlined hide-details class="cve-selector-input" append-icon="mdi-chevron-down" />
                </div>
              </template>
              <v-card class="cve-dropdown">
                <div class="cve-dropdown__search-wrapper">
                  <v-icon small color="grey">mdi-magnify</v-icon>
                  <input v-model="cveSearchQuery" type="text" placeholder="Search CVEs..." class="cve-dropdown__search-input" />
                  <v-icon v-if="cveSearchQuery" small color="grey" class="cve-dropdown__clear-search" @click="cveSearchQuery = ''">mdi-close</v-icon>
                </div>
                <div class="cve-dropdown__actions">
                  <v-btn x-small text color="primary" :disabled="allCvesSelected" @click="selectAllCves">
                    <v-icon x-small left>mdi-checkbox-multiple-marked</v-icon>Select All
                  </v-btn>
                  <v-btn x-small text color="error" :disabled="selectedCves.length === 0" @click="clearCveSelection">
                    <v-icon x-small left>mdi-close-circle</v-icon>Clear
                  </v-btn>
                </div>
                <v-divider />
                <v-list dense class="cve-dropdown__list">
                  <v-list-item v-for="cve in filteredCvesList" :key="cve" class="cve-dropdown__item" @click="toggleCve(cve)">
                    <v-list-item-action class="mr-2">
                      <v-checkbox :input-value="selectedCves.includes(cve)" color="primary" dense hide-details @click.stop="toggleCve(cve)" />
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title class="cve-dropdown__cve-text">{{ cve }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item v-if="filteredCvesList.length === 0">
                    <v-list-item-content>
                      <v-list-item-title class="text-center grey--text">No CVEs found</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <v-divider />
                <div class="cve-dropdown__footer">
                  <span class="cve-dropdown__count">{{ selectedCves.length }} / {{ availableCves.length }} selected</span>
                  <v-btn small color="primary" depressed @click="fullscreenCveMenuOpen = false">Done</v-btn>
                </div>
              </v-card>
            </v-menu>

            <!-- Path Mode Selector (Chip Dropdown) -->
            <v-menu offset-y :close-on-content-click="true" min-width="160">
              <template v-slot:activator="{ on, attrs }">
                <div class="path-mode-chip" v-bind="attrs" v-on="on">
                  <v-icon small class="path-mode-chip__icon">{{ pathModeOptions[flowPathMode].icon }}</v-icon>
                  <span class="path-mode-chip__label">{{ pathModeOptions[flowPathMode].label }}</span>
                  <v-icon small class="path-mode-chip__chevron">mdi-chevron-down</v-icon>
                </div>
              </template>
              <v-card class="path-mode-menu">
                <v-list dense class="path-mode-list">
                  <v-list-item
                    v-for="(option, key) in pathModeOptions"
                    :key="key"
                    class="path-mode-item"
                    :class="{ 'path-mode-item--active': flowPathMode === key }"
                    @click="flowPathMode = key"
                  >
                    <v-list-item-icon class="path-mode-item__icon-wrapper">
                      <v-icon small :color="flowPathMode === key ? 'primary' : ''">{{ option.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="path-mode-item__title">{{ option.label }}</v-list-item-title>
                      <v-list-item-subtitle class="path-mode-item__path">{{ option.path }}</v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action v-if="flowPathMode === key" class="path-mode-item__check">
                      <v-icon small color="primary">mdi-check</v-icon>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>

            <!-- Settings Controls -->
            <div class="settings-controls">
              <v-menu offset-y :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small class="settings-btn" v-bind="attrs" v-on="on">
                    <v-icon>mdi-cog</v-icon>
                  </v-btn>
                </template>
                <div class="settings-menu" :class="{ 'light-theme': !isDarkTheme }">
                  <!-- Color Palette Selectors -->
                  <div class="settings-menu__title">Color Palettes</div>
                  <div class="palette-selects">
                    <div class="palette-select-col">
                      <label class="palette-select-label">
                        <v-icon x-small class="palette-label-icon">mdi-white-balance-sunny</v-icon>Light
                      </label>
                      <v-select
                        :value="storeLightPalette"
                        :items="lightPaletteItems"
                        item-text="name"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                        class="palette-select"
                        @change="setLightPalette"
                      >
                        <template v-slot:item="{ item }">
                          <div class="palette-dropdown-item">
                            <span class="palette-dropdown-name">{{ item.name }}</span>
                            <span class="palette-dropdown-dots">
                              <span v-for="c in item.preview" :key="c" class="palette-dot" :style="{ backgroundColor: c }" />
                            </span>
                          </div>
                        </template>
                        <template v-slot:selection="{ item }">
                          <div class="palette-dropdown-item">
                            <span class="palette-dropdown-name">{{ item.name }}</span>
                            <span class="palette-dropdown-dots">
                              <span v-for="c in item.preview" :key="c" class="palette-dot" :style="{ backgroundColor: c }" />
                            </span>
                          </div>
                        </template>
                      </v-select>
                    </div>
                    <div class="palette-select-col">
                      <label class="palette-select-label">
                        <v-icon x-small class="palette-label-icon">mdi-moon-waning-crescent</v-icon>Dark
                      </label>
                      <v-select
                        :value="storeDarkPalette"
                        :items="darkPaletteItems"
                        item-text="name"
                        item-value="id"
                        dense
                        outlined
                        hide-details
                        class="palette-select"
                        @change="setDarkPalette"
                      >
                        <template v-slot:item="{ item }">
                          <div class="palette-dropdown-item">
                            <span class="palette-dropdown-name">{{ item.name }}</span>
                            <span class="palette-dropdown-dots">
                              <span v-for="c in item.preview" :key="c" class="palette-dot" :style="{ backgroundColor: c }" />
                            </span>
                          </div>
                        </template>
                        <template v-slot:selection="{ item }">
                          <div class="palette-dropdown-item">
                            <span class="palette-dropdown-name">{{ item.name }}</span>
                            <span class="palette-dropdown-dots">
                              <span v-for="c in item.preview" :key="c" class="palette-dot" :style="{ backgroundColor: c }" />
                            </span>
                          </div>
                        </template>
                      </v-select>
                    </div>
                  </div>
                  <v-divider class="my-3" />
                  <!-- Flow View Settings -->
                  <template v-if="activeView === 'v1'">
                    <div class="settings-menu__title">Flow Layout</div>
                    <div class="settings-slider">
                      <label>Node Width</label>
                      <v-slider v-model="flowSettings.nodeWidth" :min="6" :max="20" hide-details dense @change="updateFlowSettings" />
                    </div>
                    <div class="settings-slider">
                      <label>Node Spacing</label>
                      <v-slider v-model="flowSettings.nodePadding" :min="8" :max="40" hide-details dense @change="updateFlowSettings" />
                    </div>
                    <div class="settings-slider">
                      <label>Link Opacity</label>
                      <v-slider v-model="flowSettings.linkOpacity" :min="10" :max="80" hide-details dense @change="updateFlowSettings" />
                    </div>
                    <div class="settings-slider">
                      <label>Label Size</label>
                      <v-slider v-model="flowSettings.labelSize" :min="9" :max="16" hide-details dense @change="updateFlowSettings" />
                    </div>
                  </template>
                  <!-- Graph View Settings -->
                  <template v-else>
                    <div class="settings-two-column">
                      <div class="settings-column">
                        <div class="settings-menu__title">Node Sizes</div>
                        <div class="settings-slider">
                          <label>Organization</label>
                          <v-slider v-model="nodeSizes.org" :min="20" :max="80" hide-details dense @change="updateNodeSizes" />
                        </div>
                        <div class="settings-slider">
                          <label>Asset</label>
                          <v-slider v-model="nodeSizes.asset" :min="8" :max="40" hide-details dense @change="updateNodeSizes" />
                        </div>
                        <div class="settings-slider">
                          <label>Technology</label>
                          <v-slider v-model="nodeSizes.tech" :min="8" :max="40" hide-details dense @change="updateNodeSizes" />
                        </div>
                        <div class="settings-slider">
                          <label>CVE</label>
                          <v-slider v-model="nodeSizes.cve" :min="10" :max="50" hide-details dense @change="updateNodeSizes" />
                        </div>
                        <div class="settings-slider">
                          <label>CWE</label>
                          <v-slider v-model="nodeSizes.cwe" :min="8" :max="40" hide-details dense @change="updateNodeSizes" />
                        </div>
                        <div class="settings-slider">
                          <label>CAPEC</label>
                          <v-slider v-model="nodeSizes.capec" :min="6" :max="35" hide-details dense @change="updateNodeSizes" />
                        </div>
                        <div class="settings-slider">
                          <label>TTP</label>
                          <v-slider v-model="nodeSizes.ttp" :min="8" :max="40" hide-details dense @change="updateNodeSizes" />
                        </div>
                        <div class="settings-slider">
                          <label>APT Group</label>
                          <v-slider v-model="nodeSizes.apt" :min="10" :max="50" hide-details dense @change="updateNodeSizes" />
                        </div>
                      </div>
                      <div class="settings-column">
                        <div class="settings-menu__title">Ring Distances</div>
                        <div class="settings-slider">
                          <label>Asset Ring</label>
                          <v-slider v-model="ringRadii.asset" :min="40" :max="150" hide-details dense @change="updateRingRadii" />
                        </div>
                        <div class="settings-slider">
                          <label>Tech Ring</label>
                          <v-slider v-model="ringRadii.tech" :min="80" :max="200" hide-details dense @change="updateRingRadii" />
                        </div>
                        <div class="settings-slider">
                          <label>CVE Ring</label>
                          <v-slider v-model="ringRadii.cve" :min="120" :max="280" hide-details dense @change="updateRingRadii" />
                        </div>
                        <div class="settings-slider">
                          <label>CWE Ring</label>
                          <v-slider v-model="ringRadii.cwe" :min="180" :max="380" hide-details dense @change="updateRingRadii" />
                        </div>
                        <div class="settings-slider">
                          <label>CAPEC Ring</label>
                          <v-slider v-model="ringRadii.capec" :min="250" :max="480" hide-details dense @change="updateRingRadii" />
                        </div>
                        <div class="settings-slider">
                          <label>TTP Ring</label>
                          <v-slider v-model="ringRadii.ttp" :min="320" :max="560" hide-details dense @change="updateRingRadii" />
                        </div>
                        <div class="settings-slider">
                          <label>APT Ring</label>
                          <v-slider v-model="ringRadii.apt" :min="400" :max="700" hide-details dense @change="updateRingRadii" />
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </v-menu>
            </div>

            <!-- Theme Toggle -->
            <v-btn icon small class="theme-toggle-btn" @click="toggleTheme">
              <v-icon>{{ isDarkTheme ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent' }}</v-icon>
            </v-btn>

            <!-- Exit Fullscreen -->
            <v-btn icon small class="close-fullscreen-btn" @click="closeFullscreen">
              <v-icon>mdi-fullscreen-exit</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="fullscreen-content">
          <!-- V1: Sankey Flow View -->
          <div v-if="activeView === 'v1'" class="view-container">
            <c-v-e-attack-visualizer
              :key="`flow-fs-${selectedCves.join(',')}-${flowPathMode}-${activeColorPalette}`"
              ref="v1Fullscreen"
              :insights-data="insightsData"
              :is-embedded="true"
              :hide-header="true"
              :external-selected-cves="selectedCves"
              :is-dark="isDarkTheme"
              :flow-settings="flowSettings"
              :asset-name="effectiveAssetName"
              :asset-correlation="effectiveAssetCorrelation"
              :filtered-visualization="filteredVisualization"
              :path-mode="flowPathMode"
              :is-loading="isLoading"
              @fullscreen-close="closeFullscreen"
            />
          </div>
          <!-- V2: Graph View -->
          <div v-show="activeView === 'v2'" class="view-container graph-view">
            <!-- Floating Search -->
            <div class="floating-search" :class="{ 'dark-theme': isDarkTheme }">
              <v-icon small class="floating-search__icon">mdi-magnify</v-icon>
              <input
                ref="nodeSearchInputFullscreen"
                v-model="nodeSearchQuery"
                type="text"
                class="floating-search__input"
                placeholder="Search nodes..."
                @keyup.enter="searchAndHighlightNode"
                @input="onNodeSearchInput"
                @focus="onNodeSearchInput"
              />
              <v-icon
                v-if="nodeSearchQuery"
                small
                class="floating-search__clear"
                @click="clearNodeSearch"
              >mdi-close</v-icon>
              <!-- Search Results Dropdown -->
              <div v-if="showNodeSearchResults && nodeSearchResults.length > 0" class="floating-search-results">
                <div
                  v-for="result in nodeSearchResults"
                  :key="result.id"
                  class="floating-search-results__item"
                  @click="selectSearchResult(result)"
                >
                  <span class="floating-search-results__type">{{ result.type }}</span>
                  <span class="floating-search-results__label">{{ result.label || result.id }}</span>
                </div>
              </div>
              <div v-else-if="showNodeSearchResults && nodeSearchQuery.length >= 2" class="floating-search-results">
                <div class="floating-search-results__empty">No nodes found</div>
              </div>
            </div>
            <threat-visualizer-d3
              :key="`graph-fs-${effectiveAssetName}-${isDarkTheme}-${activeColorPalette}`"
              ref="v2Fullscreen"
              :insights-data="insightsData"
              :node-sizes="nodeSizes"
              :ring-radii="ringRadii"
              :selected-cves="selectedCves"
              :is-dark="isDarkTheme"
              :asset-name="effectiveAssetName"
              :asset-correlation="effectiveAssetCorrelation"
              :filtered-visualization="filteredVisualization"
              :path-mode="flowPathMode"
              :is-loading="isLoading"
              @node-select="handleNodeSelect"
            />
            <transition name="slide-panel">
              <resizable-panel
                v-if="selectedNodeInfo"
                class="details-panel overlay-panel"
                :initial-width="380"
                :min-width="280"
                :max-width-percent="80"
                position="left"
                :is-dark="isDarkTheme"
                storage-key="threat-visualizer-panel-width-fullscreen"
              >
                <c-v-e-info-panel
                  :info="selectedNodeInfo"
                  :attack-paths-map="attackPathsMap"
                  :apt-groups="computedAptGroups"
                  :insights-data="insightsData"
                  :is-dark="isDarkTheme"
                  @close="clearSelection"
                />
              </resizable-panel>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <!-- Normal View Header -->
    <div class="visualizer-header">
      <div class="header-left">
        <div class="visualizer-title">{{ $t('domain.insights.cve.attack.title') }}</div>
        <div class="visualizer-subtitle">{{ dynamicSubtitle }}</div>
      </div>
      <div class="header-right">
        <!-- View Tabs -->
        <div class="view-tabs">
          <button class="tab-btn" :class="{ active: activeView === 'v1' }" @click="activeView = 'v1'">
            <v-icon small class="mr-1">mdi-chart-sankey-variant</v-icon>
            Flow
          </button>
          <button class="tab-btn" :class="{ active: activeView === 'v2' }" @click="activeView = 'v2'">
            <v-icon small class="mr-1">mdi-graph-outline</v-icon>
            Graph
          </button>
        </div>

        <!-- Path Mode Selector (Chip Dropdown) -->
        <v-menu offset-y :close-on-content-click="true" min-width="160">
          <template v-slot:activator="{ on, attrs }">
            <div class="path-mode-chip" v-bind="attrs" v-on="on">
              <v-icon small class="path-mode-chip__icon">{{ pathModeOptions[flowPathMode].icon }}</v-icon>
              <span class="path-mode-chip__label">{{ pathModeOptions[flowPathMode].label }}</span>
              <v-icon small class="path-mode-chip__chevron">mdi-chevron-down</v-icon>
            </div>
          </template>
          <v-card class="path-mode-menu">
            <v-list dense class="path-mode-list">
              <v-list-item
                v-for="(option, key) in pathModeOptions"
                :key="key"
                class="path-mode-item"
                :class="{ 'path-mode-item--active': flowPathMode === key }"
                @click="flowPathMode = key"
              >
                <v-list-item-icon class="path-mode-item__icon-wrapper">
                  <v-icon small :color="flowPathMode === key ? 'primary' : ''">{{ option.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="path-mode-item__title">{{ option.label }}</v-list-item-title>
                  <v-list-item-subtitle class="path-mode-item__path">{{ option.path }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action v-if="flowPathMode === key" class="path-mode-item__check">
                  <v-icon small color="primary">mdi-check</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>

        <!-- CVE Selector -->
        <v-menu v-model="cveMenuOpen" :close-on-content-click="false" offset-y max-width="320" min-width="280">
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="cve-selector-wrapper">
              <v-text-field :value="selectorDisplayText" readonly dense outlined hide-details class="cve-selector-input" append-icon="mdi-chevron-down" />
            </div>
          </template>
          <v-card class="cve-dropdown">
            <div class="cve-dropdown__search-wrapper">
              <v-icon small color="grey">mdi-magnify</v-icon>
              <input v-model="cveSearchQuery" type="text" placeholder="Search CVEs..." class="cve-dropdown__search-input" />
              <v-icon v-if="cveSearchQuery" small color="grey" class="cve-dropdown__clear-search" @click="cveSearchQuery = ''">mdi-close</v-icon>
            </div>
            <div class="cve-dropdown__actions">
              <v-btn x-small text color="primary" :disabled="allCvesSelected" @click="selectAllCves">
                <v-icon x-small left>mdi-checkbox-multiple-marked</v-icon>Select All
              </v-btn>
              <v-btn x-small text color="error" :disabled="selectedCves.length === 0" @click="clearCveSelection">
                <v-icon x-small left>mdi-close-circle</v-icon>Clear
              </v-btn>
            </div>
            <v-divider />
            <v-list dense class="cve-dropdown__list">
              <v-list-item v-for="cve in filteredCvesList" :key="cve" class="cve-dropdown__item" @click="toggleCve(cve)">
                <v-list-item-action class="mr-2">
                  <v-checkbox :input-value="selectedCves.includes(cve)" color="primary" dense hide-details @click.stop="toggleCve(cve)" />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="cve-dropdown__cve-text">{{ cve }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="filteredCvesList.length === 0">
                <v-list-item-content>
                  <v-list-item-title class="text-center grey--text">No CVEs found</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-divider />
            <div class="cve-dropdown__footer">
              <span class="cve-dropdown__count">{{ selectedCves.length }} / {{ availableCves.length }} selected</span>
              <v-btn small color="primary" depressed @click="cveMenuOpen = false">Done</v-btn>
            </div>
          </v-card>
        </v-menu>
        <!-- Theme Toggle -->
        <v-btn icon small class="theme-toggle-btn" @click="toggleTheme">
          <v-icon>{{ isDarkTheme ? 'mdi-white-balance-sunny' : 'mdi-moon-waning-crescent' }}</v-icon>
        </v-btn>
        <!-- Fullscreen -->
        <v-btn icon small @click="openFullscreen" :disabled="!hasData">
          <v-icon>mdi-fullscreen</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Visualization Body -->
    <div class="visualizer-body">
      <!-- No CVEs message -->
      <div v-if="availableCves.length === 0" class="no-data-state">
        <v-icon large color="grey lighten-1">mdi-alert-circle-outline</v-icon>
        <div class="no-data-text">No CVEs available for visualization</div>
      </div>

      <!-- V1: Sankey Flow View -->
      <div v-else-if="activeView === 'v1'" class="view-container">
        <c-v-e-attack-visualizer
          :key="`flow-${selectedCves.join(',')}-${flowPathMode}-${activeColorPalette}`"
          ref="v1Normal"
          :insights-data="insightsData"
          :is-embedded="true"
          :hide-header="true"
          :external-selected-cves="selectedCves"
          :is-dark="isDarkTheme"
          :flow-settings="flowSettings"
          :asset-name="effectiveAssetName"
          :asset-correlation="effectiveAssetCorrelation"
          :filtered-visualization="filteredVisualization"
          :path-mode="flowPathMode"
          :is-loading="isLoading"
        />
      </div>

      <!-- V2: Graph View -->
      <div v-else-if="activeView === 'v2'" class="view-container graph-view">
        <div v-if="selectedCves.length === 0" class="no-data-state">
          <v-icon large color="grey lighten-1">mdi-graph-outline</v-icon>
          <div class="no-data-text">Select CVEs to see the threat graph</div>
        </div>
        <template v-else>
          <!-- Floating Search -->
          <div class="floating-search" :class="{ 'dark-theme': isDarkTheme }">
            <v-icon small class="floating-search__icon">mdi-magnify</v-icon>
            <input
              ref="nodeSearchInputNormal"
              v-model="nodeSearchQuery"
              type="text"
              class="floating-search__input"
              placeholder="Search nodes..."
              @keyup.enter="searchAndHighlightNode"
              @input="onNodeSearchInput"
              @focus="onNodeSearchInput"
            />
            <v-icon
              v-if="nodeSearchQuery"
              small
              class="floating-search__clear"
              @click="clearNodeSearch"
            >mdi-close</v-icon>
            <!-- Search Results Dropdown -->
            <div v-if="showNodeSearchResults && nodeSearchResults.length > 0" class="floating-search-results">
              <div
                v-for="result in nodeSearchResults"
                :key="result.id"
                class="floating-search-results__item"
                @click="selectSearchResult(result)"
              >
                <span class="floating-search-results__type">{{ result.type }}</span>
                <span class="floating-search-results__label">{{ result.label || result.id }}</span>
              </div>
            </div>
            <div v-else-if="showNodeSearchResults && nodeSearchQuery.length >= 2" class="floating-search-results">
              <div class="floating-search-results__empty">No nodes found</div>
            </div>
          </div>
          <threat-visualizer-d3
            :key="`graph-${effectiveAssetName}-${isDarkTheme}-${activeColorPalette}`"
            ref="v2Normal"
            :insights-data="insightsData"
            :node-sizes="nodeSizes"
            :ring-radii="ringRadii"
            :selected-cves="selectedCves"
            :is-dark="isDarkTheme"
            :asset-name="effectiveAssetName"
            :asset-correlation="effectiveAssetCorrelation"
            :filtered-visualization="filteredVisualization"
            :path-mode="flowPathMode"
            :is-loading="isLoading"
            @node-select="handleNodeSelect"
          />
          <transition name="slide-panel">
            <resizable-panel
              v-if="selectedNodeInfo && !isFullscreen"
              class="details-panel overlay-panel"
              :initial-width="380"
              :min-width="300"
              :max-width-percent="80"
              position="left"
              :is-dark="isDarkTheme"
              storage-key="threat-visualizer-panel-width"
            >
              <c-v-e-info-panel
                :info="selectedNodeInfo"
                :attack-paths-map="attackPathsMap"
                :apt-groups="computedAptGroups"
                :insights-data="insightsData"
                :is-dark="isDarkTheme"
                @close="clearSelection"
              />
            </resizable-panel>
          </transition>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CVEAttackVisualizer from '../cve-attack-visualizer/CVEAttackVisualizer.vue';
import ThreatVisualizerD3 from './ThreatVisualizerD3.vue';
import CVEInfoPanel from '../cve-attack-visualizer/CVEInfoPanel.vue';
import ResizablePanel from '../ResizablePanel.vue';
import {
  extractVisualizationData,
  NODE_SIZES,
  RING_RADII,
} from './threatVisualizerEngine';
import { extractCvesFromInsights, buildAttackPathsMap } from '../cve-attack-visualizer/cveVisualizerUtils';
import { PATH_MODE_OPTIONS } from '../shared';
import { LIGHT_PALETTES, DARK_PALETTES } from '../shared/colorPalettes';

export default {
  name: 'ThreatVisualizer',
  components: {
    CVEAttackVisualizer,
    ThreatVisualizerD3,
    CVEInfoPanel,
    ResizablePanel,
  },
  props: {
    insightsData: {
      type: Object,
      default: null,
    },
    assetName: {
      type: String,
      default: null,
    },
    assetCorrelation: {
      type: Object,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeView: 'v2',
      isFullscreen: false,
      selectedNode: null,
      connections: [],
      allNodes: [],
      selectedCves: [],
      cveMenuOpen: false,
      fullscreenCveMenuOpen: false,
      cveSearchQuery: '',
      nodeSizes: {
        org: NODE_SIZES.org,
        asset: NODE_SIZES.asset,
        tech: NODE_SIZES.tech,
        cve: NODE_SIZES.cve,
        cwe: NODE_SIZES.cwe,
        capec: NODE_SIZES.capec,
        ttp: NODE_SIZES.ttp,
        apt: NODE_SIZES.apt,
      },
      ringRadii: {
        asset: RING_RADII.asset,
        tech: RING_RADII.tech,
        cve: RING_RADII.cve,
        cwe: RING_RADII.cwe,
        capec: RING_RADII.capec,
        ttp: RING_RADII.ttp,
        apt: RING_RADII.apt,
      },
      flowSettings: {
        nodeWidth: 10,
        nodePadding: 16,
        linkOpacity: 35,
        labelSize: 11,
      },
      flowPathMode: 'full',
      isSidebarCollapsed: false,
      nodeSearchQuery: '',
      showNodeSearchResults: false,
      highlightedNodeId: null,
    };
  },
  computed: {
    ...mapGetters('theme', ['isDarkMode']),
    ...mapGetters('theme', {
      storeLightPalette: 'lightPalette',
      storeDarkPalette: 'darkPalette',
      activeColorPalette: 'activePalette',
    }),
    pathModeOptions() {
      return PATH_MODE_OPTIONS;
    },
    isDarkTheme() {
      return this.isDarkMode;
    },
    lightPaletteItems() {
      return Object.entries(LIGHT_PALETTES).map(([id, p]) => ({
        id,
        name: p.name,
        preview: Object.values(p.colors),
      }));
    },
    darkPaletteItems() {
      return Object.entries(DARK_PALETTES).map(([id, p]) => ({
        id,
        name: p.name,
        preview: Object.values(p.colors),
      }));
    },
    availableCves() {
      return extractCvesFromInsights(this.insightsData);
    },
    effectiveAssetName() {
      // Use prop if provided, otherwise try to extract from data
      if (this.assetName) return this.assetName;
      if (this.insightsData?.organization?.name) return this.insightsData.organization.name;
      return 'Organization';
    },
    effectiveAssetCorrelation() {
      // Use prop if provided, otherwise extract from insightsData
      if (this.assetCorrelation && Object.keys(this.assetCorrelation).length > 0) {
        return this.assetCorrelation;
      }
      // Fallback: extract from insightsData
      return this.insightsData?.assetCorrelation || {};
    },
    filteredCvesList() {
      if (!this.cveSearchQuery) return this.availableCves;
      const query = this.cveSearchQuery.toLowerCase();
      return this.availableCves.filter(cve => cve.toLowerCase().includes(query));
    },
    selectorDisplayText() {
      if (this.selectedCves.length === 0) return 'Select CVEs...';
      if (this.selectedCves.length === 1) return this.selectedCves[0];
      return `${this.selectedCves.length} CVEs selected`;
    },
    allCvesSelected() {
      return this.selectedCves.length === this.availableCves.length;
    },
    hasData() {
      return this.selectedCves.length > 0;
    },
    sidebarWidth() {
      // Sidebar width: 120px when expanded, 12px when collapsed
      return this.isSidebarCollapsed ? '12px' : '120px';
    },
    /**
     * Pre-computed filtered visualization data based on selected CVEs.
     * This is computed once and shared between Flow and Graph views.
     */
    filteredVisualization() {
      const visualization = this.effectiveAssetCorrelation?.visualization;
      if (!visualization || !visualization.nodes || visualization.nodes.length === 0) {
        return null;
      }

      if (this.selectedCves.length === 0) {
        return { nodes: [], links: [] };
      }

      const selectedCveSet = new Set(this.selectedCves);

      // Track originally selected CVE node IDs
      const selectedCveNodeIds = new Set();
      visualization.nodes.forEach(node => {
        if (node.type === 'cve') {
          if (selectedCveSet.has(node.id) || selectedCveSet.has(node.name)) {
            selectedCveNodeIds.add(node.id);
          }
        }
      });

      // If no CVEs matched with type check, try matching any node
      if (selectedCveNodeIds.size === 0) {
        visualization.nodes.forEach(node => {
          if (selectedCveSet.has(node.id) || selectedCveSet.has(node.name)) {
            selectedCveNodeIds.add(node.id);
          }
        });
      }

      // If still no matches, return empty
      if (selectedCveNodeIds.size === 0) {
        return { nodes: [], links: [] };
      }

      // Build link maps for efficient traversal
      const linksByTarget = new Map();
      const linksBySource = new Map();
      visualization.links.forEach(link => {
        if (!linksByTarget.has(link.target)) linksByTarget.set(link.target, []);
        linksByTarget.get(link.target).push(link.source);
        if (!linksBySource.has(link.source)) linksBySource.set(link.source, []);
        linksBySource.get(link.source).push(link.target);
      });

      // Get node types
      const nodeTypeMap = new Map();
      visualization.nodes.forEach(node => {
        nodeTypeMap.set(node.id, node.type);
      });

      // Start with selected CVEs
      const finalNodeIds = new Set(selectedCveNodeIds);

      // Always include ORG node
      visualization.nodes.forEach(node => {
        if (node.type === 'org') {
          finalNodeIds.add(node.id);
        }
      });

      // Traverse BACKWARD from selected CVEs (to get TECH, ASSET)
      // Stop at ORG, don't add other CVEs
      const backwardQueue = [...selectedCveNodeIds];
      while (backwardQueue.length > 0) {
        const nodeId = backwardQueue.shift();
        const sources = linksByTarget.get(nodeId) || [];
        sources.forEach(sourceId => {
          if (!finalNodeIds.has(sourceId)) {
            const sourceType = nodeTypeMap.get(sourceId);
            if (sourceType !== 'cve') {
              finalNodeIds.add(sourceId);
              if (sourceType !== 'org') {
                backwardQueue.push(sourceId);
              }
            }
          }
        });
      }

      // Traverse FORWARD from selected CVEs (to get CWE, CAPEC, TTP, APT)
      const forwardQueue = [...selectedCveNodeIds];
      while (forwardQueue.length > 0) {
        const nodeId = forwardQueue.shift();
        const targets = linksBySource.get(nodeId) || [];
        targets.forEach(targetId => {
          if (!finalNodeIds.has(targetId)) {
            const targetType = nodeTypeMap.get(targetId);
            if (targetType !== 'cve') {
              finalNodeIds.add(targetId);
              forwardQueue.push(targetId);
            }
          }
        });
      }

      // Build filtered result
      const filteredNodes = visualization.nodes.filter(n => finalNodeIds.has(n.id));
      const filteredLinks = visualization.links.filter(
        l => finalNodeIds.has(l.source) && finalNodeIds.has(l.target),
      );

      return { nodes: filteredNodes, links: filteredLinks };
    },
    attackPathsMap() {
      return buildAttackPathsMap(this.insightsData);
    },
    computedAptGroups() {
      const groups = this.insightsData?.groups;
      if (groups && Array.isArray(groups) && groups.length > 0) return groups;
      const aptGroups = this.insightsData?.insights?.relevant_APT_groups;
      return aptGroups && aptGroups !== 'N/A' ? aptGroups : [];
    },
    selectedNodeInfo() {
      // Transform selectedNode into the format expected by CVEInfoPanel
      if (!this.selectedNode) return null;
      const node = this.selectedNode;

      // Get all connected nodes by type
      const connectedAssets = this.getConnectedNodesByType(node.id, 'asset');
      const connectedTechs = this.getConnectedNodesByType(node.id, 'tech');
      const connectedCves = this.getConnectedNodesByType(node.id, 'cve');
      const connectedCwes = this.getConnectedNodesByType(node.id, 'cwe');
      const connectedCapecs = this.getConnectedNodesByType(node.id, 'capec');
      const connectedTtps = this.getConnectedNodesByType(node.id, 'ttp');
      const connectedApts = this.getConnectedNodesByType(node.id, 'apt');

      let assets = [];
      let technologies = [];
      let cves = [];
      let cwes = [];
      let capecs = [];
      let techniques = [];

      // Include the node itself in its category
      if (node.type === 'asset') {
        assets = [node.label || node.id];
        technologies = connectedTechs;
        cves = connectedCves;
        cwes = connectedCwes;
        capecs = connectedCapecs;
        techniques = connectedTtps;
      } else if (node.type === 'tech') {
        technologies = [node.label || node.id];
        assets = connectedAssets;
        cves = connectedCves;
        cwes = connectedCwes;
        capecs = connectedCapecs;
        techniques = connectedTtps;
      } else if (node.type === 'cve') {
        cves = [node.label || node.id];
        // For CVE, get connected CWEs, CAPECs, TTPs from the attack paths
        const cveData = this.attackPathsMap[node.label || node.id];
        if (cveData) {
          cwes = cveData.cwes || [];
          capecs = cveData.capecs || [];
          techniques = cveData.techniques || [];
        } else {
          // Fallback to graph connections
          cwes = connectedCwes;
          capecs = connectedCapecs;
          techniques = connectedTtps;
        }
        assets = connectedAssets;
        technologies = connectedTechs;
      } else if (node.type === 'cwe') {
        cwes = [node.label || node.id];
        cves = connectedCves;
        capecs = connectedCapecs;
        techniques = connectedTtps;
        assets = connectedAssets;
        technologies = connectedTechs;
      } else if (node.type === 'capec') {
        capecs = [node.label || node.id];
        cwes = connectedCwes;
        techniques = connectedTtps;
        cves = connectedCves;
        assets = connectedAssets;
        technologies = connectedTechs;
      } else if (node.type === 'ttp') {
        techniques = [node.label || node.id];
        capecs = connectedCapecs;
        cwes = connectedCwes;
        cves = connectedCves;
        assets = connectedAssets;
        technologies = connectedTechs;
      } else if (node.type === 'apt') {
        // For APT nodes, get TTPs from both graph connections AND the APT's own data
        techniques = connectedTtps;
        // Also include TTPs from the APT's overlap_ttps data if not already included
        const aptTtps = node.data?.TTPs || node.data?.ttps || node.data?.overlap_ttps || [];
        aptTtps.forEach(ttp => {
          const ttpId = typeof ttp === 'string' ? ttp.match(/^(T\d+(?:\.\d+)?)/)?.[1] : ttp.id;
          if (ttpId && !techniques.includes(ttpId)) {
            techniques.push(ttpId);
          }
        });
        cves = connectedCves;
        assets = connectedAssets;
        technologies = connectedTechs;
      } else {
        // org or other types
        assets = connectedAssets;
        technologies = connectedTechs;
        cves = connectedCves;
        cwes = connectedCwes;
        capecs = connectedCapecs;
        techniques = connectedTtps;
      }

      return {
        name: node.label || node.id,
        type: node.type,
        assets,
        technologies,
        cves,
        cwes,
        capecs,
        techniques,
        connectedApts,
        connectedNodes: [...assets, ...technologies, ...cves, ...cwes, ...capecs, ...techniques, ...connectedApts],
        nodeData: node.data,
      };
    },
    filteredInsightsData() {
      // Filter insights data to only include selected CVEs
      if (!this.insightsData || this.selectedCves.length === 0) return null;
      const filtered = JSON.parse(JSON.stringify(this.insightsData));
      if (filtered.organization?.org_cves) {
        filtered.organization.org_cves = filtered.organization.org_cves.filter(
          cve => this.selectedCves.includes(cve.name),
        );
      }
      return filtered;
    },
    dynamicSubtitle() {
      return this.pathModeOptions[this.flowPathMode]?.path || this.pathModeOptions.full.path;
    },
    nodeSearchResults() {
      if (!this.nodeSearchQuery || this.nodeSearchQuery.length < 2) return [];
      const query = this.nodeSearchQuery.toLowerCase();

      // Only search nodes actually rendered in the D3 graph
      const graphRef = this.isFullscreen ? this.$refs.v2Fullscreen : this.$refs.v2Normal;
      const graphNodes = graphRef?.nodes || [];

      // Use graph nodes if available, fall back to allNodes only if graph not ready
      const searchableNodes = graphNodes.length > 0 ? graphNodes : this.allNodes;

      const results = [];
      searchableNodes.forEach(node => {
        const label = (node.label || node.id || '').toLowerCase();
        if (label.includes(query)) {
          results.push(node);
        }
      });

      return results.slice(0, 15);
    },
  },
  watch: {
    insightsData: {
      handler() {
        this.updateGraphData();
      },
      deep: true,
      immediate: true,
    },
    selectedCves() {
      this.updateGraphData();
    },
    availableCves: {
      handler(newCves, oldCves) {
        // When available CVEs change (domain switch), reset and auto-select all
        // Check if the CVE list actually changed (not just initial load)
        const cveListChanged = oldCves && JSON.stringify(newCves) !== JSON.stringify(oldCves);

        if (newCves && newCves.length > 0) {
          if (this.selectedCves.length === 0 || cveListChanged) {
            // Auto-select all CVEs when they become available or when domain changes
            this.selectedCves = [...newCves];
          }
        } else {
          // No CVEs available, clear selection
          this.selectedCves = [];
        }
      },
      immediate: true,
    },
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.handleClickOutside);
    this.detectSidebarState();
    this.setupSidebarObserver();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.removeEventListener('click', this.handleClickOutside);
    if (this.sidebarObserver) {
      this.sidebarObserver.disconnect();
    }
  },
  methods: {
    detectSidebarState() {
      const sidebar = document.querySelector('.sidebar-fixed');
      if (sidebar) {
        this.isSidebarCollapsed = sidebar.classList.contains('collapsed');
      }
    },
    setupSidebarObserver() {
      const sidebar = document.querySelector('.sidebar-fixed');
      if (sidebar) {
        this.sidebarObserver = new MutationObserver(() => {
          this.isSidebarCollapsed = sidebar.classList.contains('collapsed');
        });
        this.sidebarObserver.observe(sidebar, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }
    },
    selectAllCves() {
      this.selectedCves = [...this.availableCves];
    },
    clearCveSelection() {
      this.selectedCves = [];
    },
    toggleCve(cve) {
      const idx = this.selectedCves.indexOf(cve);
      if (idx > -1) {
        this.selectedCves.splice(idx, 1);
      } else {
        this.selectedCves.push(cve);
      }
    },
    updateGraphData() {
      if (!this.filteredInsightsData) {
        this.allNodes = [];
        this.connections = [];
        return;
      }
      const data = extractVisualizationData(
        this.filteredInsightsData,
        null,
        this.effectiveAssetName,
        this.effectiveAssetCorrelation,
      );
      this.allNodes = [];
      if (data.organization) {
        this.allNodes.push({ id: `ORG:${data.organization}`, type: 'org', label: data.organization });
      }

      // Add Asset nodes from correlation data
      if (data.assets && data.assets.length > 0) {
        data.assets.forEach(asset => {
          this.allNodes.push({
            id: `ASSET:${asset.id}`,
            type: 'asset',
            label: asset.name,
            data: asset,
          });
        });
      }

      // Add Technology nodes from correlation data
      if (data.technologies && data.technologies.length > 0) {
        data.technologies.forEach(tech => {
          this.allNodes.push({
            id: `TECH:${tech.id}`,
            type: 'tech',
            label: tech.name,
            data: tech,
          });
        });
      }

      data.cves.forEach(cve => {
        this.allNodes.push({
          id: cve.id, type: 'cve', label: cve.name, data: cve,
        });
      });
      // Add CWE nodes
      if (data.cwes) {
        data.cwes.forEach(cwe => {
          this.allNodes.push({
            id: cwe.id, type: 'cwe', label: cwe.name, data: cwe,
          });
        });
      }
      // Add CAPEC nodes
      if (data.capecs) {
        data.capecs.forEach(capec => {
          this.allNodes.push({
            id: capec.id, type: 'capec', label: capec.name, data: capec,
          });
        });
      }
      // Create a map of existing TTP IDs for quick lookup
      const existingTtpIds = new Set(data.ttps.map(ttp => ttp.id));

      data.ttps.forEach(ttp => {
        this.allNodes.push({
          id: ttp.id, type: 'ttp', label: ttp.name, data: ttp,
        });
      });

      // Also add TTPs from APT groups' overlap_ttps that aren't already in the graph
      data.aptGroups.forEach(apt => {
        const aptTtps = apt.TTPs || apt.ttps || apt.overlap_ttps || [];
        aptTtps.forEach(ttp => {
          const ttpId = typeof ttp === 'string' ? ttp.match(/^(T\d+(?:\.\d+)?)/)?.[1] : ttp.id;
          if (ttpId && !existingTtpIds.has(ttpId)) {
            existingTtpIds.add(ttpId);
            const ttpName = typeof ttp === 'object' ? (ttp.name || ttpId) : ttpId;
            this.allNodes.push({
              id: ttpId,
              type: 'ttp',
              label: ttpName,
              data: typeof ttp === 'object' ? ttp : { id: ttpId, name: ttpName },
            });
          }
        });
      });

      data.aptGroups.forEach(apt => {
        const aptId = apt.name || apt.APT_group || apt['APT name'];
        this.allNodes.push({
          id: `APT:${aptId}`, type: 'apt', label: aptId, data: apt, color: apt.color,
        });
      });

      // Build connections
      this.connections = [];

      // Org to Asset connections (if assets exist)
      if (data.orgToAssets && data.orgToAssets.length > 0) {
        data.orgToAssets.forEach(assetId => {
          this.connections.push({ source: 'ORG', target: `ASSET:${assetId}` });
        });
      }

      // Asset to Tech connections
      if (data.assetToTechs) {
        Object.entries(data.assetToTechs).forEach(([assetId, techIds]) => {
          techIds.forEach(techId => {
            this.connections.push({ source: `ASSET:${assetId}`, target: `TECH:${techId}` });
          });
        });
      }

      // Tech to CVE connections
      if (data.techToCves) {
        Object.entries(data.techToCves).forEach(([techId, cveIds]) => {
          cveIds.forEach(cveId => {
            this.connections.push({ source: `TECH:${techId}`, target: cveId });
          });
        });
      }

      // If no asset correlation, connect Org directly to CVEs
      if (!data.assets || data.assets.length === 0) {
        data.cves.forEach(cve => {
          this.connections.push({ source: 'ORG', target: cve.id });
        });
      }

      // CVE to CWE connections
      if (data.cveToWeaknesses) {
        Object.entries(data.cveToWeaknesses).forEach(([cveId, cweIds]) => {
          cweIds.forEach(cweId2 => {
            this.connections.push({ source: cveId, target: cweId2 });
          });
        });
      }

      // CWE to CAPEC connections
      if (data.cweToCapecs) {
        Object.entries(data.cweToCapecs).forEach(([cweId, capecIds]) => {
          capecIds.forEach(capecId => {
            this.connections.push({ source: cweId, target: capecId });
          });
        });
      }

      // CAPEC to TTP connections
      if (data.capecToTtps) {
        Object.entries(data.capecToTtps).forEach(([capecId, ttpIds]) => {
          ttpIds.forEach(ttpId => {
            this.connections.push({ source: capecId, target: ttpId });
          });
        });
      }

      // Direct CVE to TTP (for v1 data or when no CWE/CAPEC chain)
      if (!data.cwes || data.cwes.length === 0) {
        data.cves.forEach(cve => {
          (cve.ttps || []).forEach(ttpId => {
            this.connections.push({ source: cve.id, target: ttpId });
          });
        });
      }

      // TTP to APT connections
      data.aptGroups.forEach(apt => {
        const aptId = apt.name || apt.APT_group || apt['APT name'];
        const aptTtps = apt.TTPs || apt.ttps || apt.overlap_ttps || [];
        aptTtps.forEach(ttp => {
          const ttpIdFromApt = typeof ttp === 'string' ? ttp.match(/^(T\d+(?:\.\d+)?)/)?.[1] : ttp.id;
          if (ttpIdFromApt) {
            // TTP nodes are now guaranteed to exist (added above)
            this.connections.push({ source: ttpIdFromApt, target: aptId });
          }
        });
      });
    },
    handleKeydown(e) {
      if (e.key === 'Escape' && this.isFullscreen) this.closeFullscreen();
    },
    openFullscreen() {
      this.isFullscreen = true;
      document.body.style.overflow = 'hidden';
    },
    closeFullscreen() {
      this.isFullscreen = false;
      document.body.style.overflow = '';
    },
    handleNodeSelect(node) {
      // Simply set the selected node - toggle is handled in ThreatVisualizerD3
      this.selectedNode = node;
    },
    getConnectedNodesByType(nodeId, type) {
      const connected = new Set();
      this.connections.forEach(conn => {
        if (conn.source === nodeId) {
          const targetNode = this.allNodes.find(n => n.id === conn.target);
          if (targetNode?.type === type) connected.add(targetNode.label || targetNode.id);
        }
        if (conn.target === nodeId) {
          const sourceNode = this.allNodes.find(n => n.id === conn.source);
          if (sourceNode?.type === type) connected.add(sourceNode.label || sourceNode.id);
        }
      });
      return Array.from(connected);
    },
    clearSelection() {
      this.selectedNode = null;
      const canvasRef = this.isFullscreen ? this.$refs.v2Fullscreen : this.$refs.v2Normal;
      if (canvasRef) canvasRef.clearSelection();
    },
    updateNodeSizes() {
      const canvasRef = this.isFullscreen ? this.$refs.v2Fullscreen : this.$refs.v2Normal;
      if (canvasRef) canvasRef.setNodeSizes(this.nodeSizes);
    },
    updateRingRadii() {
      const canvasRef = this.isFullscreen ? this.$refs.v2Fullscreen : this.$refs.v2Normal;
      if (canvasRef) canvasRef.setRingRadii(this.ringRadii);
    },
    updateFlowSettings() {
      const flowRef = this.isFullscreen ? this.$refs.v1Fullscreen : this.$refs.v1Normal;
      if (flowRef) flowRef.updateSettings();
    },
    toggleTheme() {
      this.$store.dispatch('theme/toggleDarkMode');
    },
    setLightPalette(paletteId) {
      this.$store.dispatch('theme/setLightPalette', paletteId);
    },
    setDarkPalette(paletteId) {
      this.$store.dispatch('theme/setDarkPalette', paletteId);
    },
    onNodeSearchInput() {
      this.showNodeSearchResults = this.nodeSearchQuery.length >= 2;
    },
    searchAndHighlightNode() {
      if (this.nodeSearchResults.length > 0) {
        this.selectSearchResult(this.nodeSearchResults[0]);
      }
    },
    selectSearchResult(node) {
      this.showNodeSearchResults = false;
      this.highlightedNodeId = node.id;

      // Get the active graph ref
      const graphRef = this.isFullscreen ? this.$refs.v2Fullscreen : this.$refs.v2Normal;
      if (graphRef && graphRef.highlightAndCenterNode) {
        graphRef.highlightAndCenterNode(node.id);
      }

      // Refocus the search input after selection
      this.$nextTick(() => {
        const inputRef = this.isFullscreen
          ? this.$refs.nodeSearchInputFullscreen
          : this.$refs.nodeSearchInputNormal;
        if (inputRef) {
          inputRef.focus();
        }
      });
    },
    clearNodeSearch() {
      this.nodeSearchQuery = '';
      this.showNodeSearchResults = false;
      this.highlightedNodeId = null;

      // Clear highlight in graph
      const graphRef = this.isFullscreen ? this.$refs.v2Fullscreen : this.$refs.v2Normal;
      if (graphRef && graphRef.clearHighlight) {
        graphRef.clearHighlight();
      }
    },
    handleClickOutside(event) {
      // Close search results if clicking outside the floating search
      const floatingSearch = event.target.closest('.floating-search');
      if (!floatingSearch && this.showNodeSearchResults) {
        this.showNodeSearchResults = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.threat-visualizer {
  background: var(--bg-widget);
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: -12px;

  &.dark-theme {
    background: var(--bg-secondary);
  }
}

.visualizer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  flex-wrap: wrap;
  gap: 8px;
}

.header-left {
  .visualizer-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 2px;
  }
  .visualizer-subtitle {
    font-size: 11px;
    color: var(--text-secondary);
  }
}

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.view-tabs {
  display: flex;
  gap: 2px;
  background: var(--bg-secondary);
  padding: 2px;
  border-radius: 6px;
}

.tab-btn {
  display: flex;
  align-items: center;
  padding: 4px 10px;
  border: none;
  background: transparent;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { background: var(--bg-tertiary); }
  &.active {
    background: var(--bg-widget);
    color: var(--text-primary);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  .v-icon {
    font-size: 12px;
    color: inherit;
  }
}

.cve-selector-wrapper {
  cursor: pointer;
  width: 160px;
  flex-shrink: 0;
}

// Path Mode Chip Dropdown
.path-mode-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--brand-primary);
  }

  &__icon {
    color: var(--brand-primary);
    font-size: 14px;
  }

  &__label {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-primary);
    white-space: nowrap;
  }

  &__chevron {
    color: var(--text-muted);
    font-size: 14px;
    transition: transform 0.2s ease;
  }
}

.path-mode-menu {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.path-mode-list {
  padding: 4px 0;
  background: var(--bg-widget);
}

.path-mode-item {
  padding: 6px 12px;
  min-height: 36px;
  transition: background 0.15s ease;

  &:hover {
    background: var(--highlight-blue-bg);
  }

  &--active {
    background: var(--highlight-blue-bg);
  }

  &__icon-wrapper {
    margin-right: 8px;
    min-width: 20px;
  }

  &__title {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-primary);
    line-height: 1.2;
  }

  &__path {
    font-size: 10px;
    color: var(--text-muted);
    line-height: 1.2;
  }

  &__check {
    margin-left: 8px;
    min-width: auto;
  }
}

.visualizer-body {
  height: 620px;
  position: relative;
}

.view-container {
  height: 100%;
  position: relative;
  &.graph-view { position: relative; }
}

.no-data-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--text-muted);
  .no-data-text {
    margin-top: 12px;
    font-size: 14px;
  }
}

.details-panel {
  height: 100%;
  overflow: hidden;
  &.overlay-panel {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 20;
    box-shadow: -4px 0 20px rgba(0, 0, 0, 0.3);
  }
}

.slide-panel-enter-active,
.slide-panel-leave-active { transition: all 0.3s ease; }
.slide-panel-enter,
.slide-panel-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

// CVE Dropdown styles
.cve-dropdown {
  border-radius: 8px;
  overflow: hidden;
  background: var(--bg-widget);
  &__search-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: var(--bg-secondary);
  }
  &__search-input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 14px;
    outline: none;
    color: var(--text-primary);
    &::placeholder { color: var(--text-muted); }
  }
  &__clear-search { cursor: pointer; opacity: 0.6; &:hover { opacity: 1; } }
  &__actions {
    display: flex;
    justify-content: space-between;
    padding: 8px 12px;
    background: var(--bg-secondary);
  }
  &__list {
    max-height: 200px;
    overflow-y: auto;
    background: var(--bg-widget);
  }
  &__item {
    min-height: 36px;
    padding: 0 12px;
    &:hover { background: var(--highlight-blue-bg); }
  }
  &__cve-text {
    font-size: 12px;
    font-family: 'SF Mono', monospace;
    color: var(--text-primary);
  }
  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 12px;
    background: var(--bg-secondary);
  }
  &__count { font-size: 12px; color: var(--text-muted); }
}

// Fullscreen styles
.fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 120px; // Default sidebar width
  right: 0;
  bottom: 0;
  background: var(--bg-primary);
  z-index: 1999; // Below sidebar (z-index: 2000) so sidebar stays visible
  display: flex;
  flex-direction: column;
  transition: background 0.3s ease, left 0.3s ease;

  &.dark-theme {
    background: var(--bg-primary);
  }
}

.fullscreen-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.fullscreen-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: var(--bg-widget);
  border-bottom: 1px solid var(--border-primary);
  flex-shrink: 0;
  transition: background 0.3s ease, border-color 0.3s ease;

  .visualizer-title { color: var(--text-primary); }
  .visualizer-subtitle { color: var(--text-tertiary); }

  .view-tabs { background: var(--bg-secondary); }
  .tab-btn {
    color: var(--text-secondary);
    &:hover { background: var(--bg-tertiary); }
    &.active {
      background: var(--bg-widget);
      color: var(--text-primary);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  }

  .settings-btn, .theme-toggle-btn, .close-fullscreen-btn {
    background: var(--bg-secondary);
    color: var(--text-secondary);
    &:hover { background: var(--bg-tertiary); }
  }

  &.dark-theme {
    background: var(--bg-secondary);
    border-bottom-color: var(--border-primary);

    .visualizer-title { color: var(--text-primary); }
    .visualizer-subtitle { color: var(--text-muted); }

    .view-tabs { background: var(--bg-tertiary); }
    .tab-btn {
      color: var(--text-muted);
      &:hover {
        background: rgba(255, 255, 255, 0.1);
        color: var(--text-secondary);
      }
      &.active {
        background: var(--brand-primary);
        color: #fff;
        box-shadow: 0 2px 8px rgba(143, 36, 208, 0.4);
      }
    }

    .settings-btn, .theme-toggle-btn, .close-fullscreen-btn {
      background: var(--bg-tertiary);
      color: var(--text-primary);
      &:hover { background: var(--bg-secondary); }
    }
  }

  &__left {
    .visualizer-title {
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 4px;
    }
    .visualizer-subtitle {
      font-size: 13px;
    }
  }
  &__center {
    display: flex;
    align-items: center;
  }
  &__right {
    display: flex;
    align-items: center;
    gap: 12px;
  }
}

.settings-controls { display: flex; align-items: center; }

.settings-menu {
  background: var(--bg-secondary);
  padding: 16px;
  min-width: 280px;
  max-width: 520px;
  max-height: 70vh;
  overflow-y: auto;
  border-radius: 8px;

  &__title {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  &.light-theme {
    background: var(--bg-widget);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

    .settings-menu__title {
      color: var(--text-secondary);
    }

    .settings-slider label {
      color: var(--text-primary);
    }
  }
}

.palette-selects {
  display: flex;
  gap: 12px;
}

.palette-select-col {
  flex: 1;
  min-width: 0;
}

.palette-select-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.palette-label-icon {
  color: var(--text-secondary) !important;
  margin-right: 4px;
}

.palette-select {
  max-width: 100%;

  ::v-deep .v-input__slot {
    min-height: 34px !important;
    padding: 0 8px !important;
  }

  ::v-deep .v-select__slot > input {
    position: absolute;
    width: 0;
    height: 0;
    padding: 0;
    opacity: 0;
    pointer-events: none;
  }

  ::v-deep .v-select__selections {
    min-height: 34px !important;
    padding: 0 !important;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
  }

  ::v-deep .v-input__append-inner {
    margin-top: 0 !important;
    align-self: center;
  }
}

.palette-dropdown-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.palette-dropdown-name {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.palette-dropdown-dots {
  display: flex;
  gap: 2px;
}

.palette-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.settings-two-column {
  display: flex;
  gap: 24px;
}

.settings-column {
  flex: 1;
  min-width: 200px;
}

.settings-slider {
  margin-bottom: 12px;
  label {
    display: block;
    font-size: 12px;
    color: var(--text-secondary);
    margin-bottom: 4px;
  }
}

.fullscreen-content {
  flex: 1;
  overflow: hidden;
  position: relative;
  .view-container { height: 100%; position: relative; }
  .details-panel.overlay-panel { min-width: 320px; }
}

// Floating Search
.floating-search {
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  background: var(--bg-widget);
  border: 1px solid var(--border-primary);
  border-radius: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  min-width: 340px;
  max-width: 480px;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: var(--brand-primary);
    box-shadow: 0 4px 20px rgba(143, 36, 208, 0.15);
  }

  &__icon {
    color: var(--text-muted);
    flex-shrink: 0;
  }

  &__input {
    flex: 1;
    border: none;
    background: transparent;
    font-size: 14px;
    color: var(--text-primary);
    outline: none;
    min-width: 0;

    &::placeholder {
      color: var(--text-muted);
    }
  }

  &__clear {
    color: var(--text-muted);
    cursor: pointer;
    flex-shrink: 0;
    opacity: 0.6;
    transition: opacity 0.15s ease;

    &:hover {
      opacity: 1;
    }
  }

  &.dark-theme {
    background: var(--bg-secondary);
    border-color: var(--border-primary);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

    &:focus-within {
      border-color: var(--brand-primary);
      box-shadow: 0 4px 24px rgba(143, 36, 208, 0.25);
    }
  }
}

// Search results dropdown
.floating-search-results {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 6px;
  background: var(--bg-widget);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  max-height: 320px;
  overflow-y: auto;
  z-index: 31;

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: var(--highlight-blue-bg);
    }

    &:first-child {
      border-radius: 11px 11px 0 0;
    }

    &:last-child {
      border-radius: 0 0 11px 11px;
    }

    &:only-child {
      border-radius: 11px;
    }
  }

  &__type {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    padding: 3px 8px;
    border-radius: 4px;
    background: var(--bg-secondary);
    color: var(--text-muted);
    flex-shrink: 0;
    min-width: 50px;
    text-align: center;
  }

  &__label {
    font-size: 14px;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__empty {
    padding: 20px;
    text-align: center;
    font-size: 13px;
    color: var(--text-muted);
  }
}

.dark-theme .floating-search-results {
  background: var(--bg-secondary);
  border-color: var(--border-primary);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}
</style>

<style lang="scss">
#app .threat-visualizer .cve-selector-input.v-text-field {
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  pointer-events: none;
  .v-input__control { min-height: 32px; height: 32px; }
  .v-input__slot { min-height: 32px; height: 32px; margin-bottom: 0; }
  .v-text-field__details { display: none; }
  fieldset { height: 36px; }
}

// Dark mode styles for fullscreen header input
#app .fullscreen-header.dark-theme .cve-selector-input.v-text-field {
  input { color: var(--text-primary); }
  .v-input__slot {
    background: var(--bg-tertiary);
  }
  fieldset {
    border-color: var(--border-primary);
  }
  .v-icon { color: var(--text-primary); }
}

// Dark mode styles for normal header input
#app .threat-visualizer.dark-theme .cve-selector-input.v-text-field {
  input { color: var(--text-primary); }
  .v-input__slot {
    background: var(--bg-tertiary);
  }
  fieldset {
    border-color: var(--border-primary);
  }
  .v-icon { color: var(--text-primary); }
}

// Dark mode styles for tab buttons
#app .threat-visualizer.dark-theme {
  .view-tabs {
    background: var(--bg-tertiary);
  }

  .tab-btn {
    color: var(--text-muted);

    .v-icon {
      color: var(--text-muted);
    }

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: var(--text-secondary);
    }

    &.active {
      background: var(--brand-primary);
      color: #fff;
      box-shadow: 0 2px 8px rgba(143, 36, 208, 0.4);

      .v-icon {
        color: #fff;
      }
    }
  }
}

// Path mode menu dark mode styles
#app .path-mode-menu {
  .v-list {
    background: var(--bg-widget);
  }

  .v-list-item {
    &:hover {
      background: var(--highlight-blue-bg);
    }
  }

  .v-list-item__title {
    color: var(--text-primary);
    font-size: 12px;
    font-weight: 500;
  }

  .v-list-item__subtitle {
    color: var(--text-muted);
    font-size: 10px;
  }

  .v-list-item__icon {
    .v-icon {
      color: var(--text-secondary);
    }
  }

  .path-mode-item--active {
    background: var(--highlight-blue-bg);

    .v-list-item__title {
      color: var(--text-primary);
    }

    .v-list-item__icon .v-icon {
      color: var(--brand-primary);
    }
  }
}

// CVE dropdown dark mode styles
#app .cve-dropdown {
  background: var(--bg-widget);

  .v-list {
    background: var(--bg-widget);
  }

  .v-list-item {
    &:hover {
      background: var(--highlight-blue-bg);
    }
  }

  .v-list-item__title {
    color: var(--text-primary);
  }

  .v-list-item__content {
    color: var(--text-primary);
  }
}

// Dark theme specific overrides for CVE dropdown
.dark-theme #app .cve-dropdown,
#app .dark-theme .cve-dropdown,
#app .fullscreen-header.dark-theme .cve-dropdown {
  background: var(--bg-secondary);

  .v-list {
    background: var(--bg-secondary);
  }

  .v-list-item {
    color: var(--text-primary);

    &:hover {
      background: var(--highlight-blue-bg);
    }
  }

  .v-list-item__title {
    color: var(--text-primary);
  }

  .v-list-item__content {
    color: var(--text-primary);
  }

  .cve-dropdown__cve-text {
    color: var(--text-primary);
  }

  .v-input--checkbox {
    .v-label {
      color: var(--text-primary);
    }
  }
}

// Global dark mode for v-card dropdowns in threat visualizer
#app .threat-visualizer.dark-theme .v-card.cve-dropdown,
#app .fullscreen-overlay.dark-theme .v-card.cve-dropdown {
  background: var(--bg-secondary);
  color: var(--text-primary);

  .cve-dropdown__search-wrapper {
    background: var(--bg-tertiary);
  }

  .cve-dropdown__search-input {
    color: var(--text-primary);

    &::placeholder {
      color: var(--text-muted);
    }
  }

  .cve-dropdown__actions {
    background: var(--bg-tertiary);
  }

  .cve-dropdown__list {
    background: var(--bg-secondary);
  }

  .cve-dropdown__item {
    color: var(--text-primary);

    &:hover {
      background: var(--highlight-blue-bg);
    }
  }

  .cve-dropdown__cve-text {
    color: var(--text-primary);
  }

  .cve-dropdown__footer {
    background: var(--bg-tertiary);
  }

  .cve-dropdown__count {
    color: var(--text-muted);
  }

  .v-divider {
    border-color: var(--border-primary);
  }
}
</style>
